/** @file helpers to convert old-format issue comments and review summaries to new "conversation comments" API format */

import type {ConversationIssueComment, ConversationReviewSummary} from '../page-data/payloads/conversation-comments'
import type {IssueComment} from '../page-data/payloads/issue-comments'
import type {ReviewSummary} from '../page-data/payloads/review-summaries'

/**
 * Fields the conversation comments API provides but the older `issue_comments` and `review_summary_comments`
 * payloads do not.
 */
const missingFields = {
  bodyVersion: '',
  lastUserContentEdit: null,
  relayId: '',
  stafftoolsUrl: null,
  viewerCanDelete: false,
  viewerCanMinimize: false,
  viewerCanSeeMinimizeButton: false,
  viewerCanSeeUnminimizeButton: false,
  viewerCanUpdate: false,
  viewerDidAuthor: false,
} as const

/**
 * Temporary shim mapping the legacy issue comments payload onto the conversation comments model.
 */
export function buildConversationIssueComment(comment: IssueComment): ConversationIssueComment {
  return {
    ...missingFields,
    type: 'issue',
    id: Number(comment.id),
    author: {login: comment.authorLogin, avatarUrl: comment.authorAvatarUrl ?? ''},
    authorAssociation: comment.authorAssociation,
    body: comment.body,
    bodyHTML: comment.bodyHtml,
    createdAt: comment.createdAt,
    publishedAt: comment.publishedAt,
    isHidden: comment.isHidden,
    minimizedReason: comment.minimizedReason,
    reactionGroups: comment.reactionGroups,
    relayId: comment.relayId,
    viewerCanBlockFromOrg: comment.viewerCanBlockFromOrg,
    viewerCanBlockPersonally: comment.viewerCanBlockPersonally,
    viewerCanReact: comment.viewerCanReact,
    viewerCanUnblockFromOrg: comment.viewerCanUnblockFromOrg,
    viewerCanUnblockPersonally: comment.viewerCanUnblockPersonally,
    repository: comment.repository,
    url: comment.url,
  }
}

export function buildConversationIssueComments(comments: readonly IssueComment[]): ConversationIssueComment[] {
  return comments.map(buildConversationIssueComment)
}

/**
 * Temporary shim mapping the legacy review summaries payload onto the conversation comments model.
 */
export function buildConversationReviewSummary(summary: ReviewSummary): ConversationReviewSummary {
  return {
    ...missingFields,
    type: 'review_summary',
    id: Number(summary.id),
    author: {login: summary.authorLogin, avatarUrl: summary.authorAvatarUrl ?? ''},
    authorAssociation: summary.authorAssociation,
    body: summary.body,
    bodyHTML: summary.bodyHtml,
    createdAt: summary.submittedAt,
    publishedAt: summary.submittedAt,
    isHidden: summary.isHidden,
    minimizedReason: summary.minimizedReason,
    onBehalfOfTeams: summary.onBehalfOfTeams,
    reactionGroups: summary.reactionGroups,
    state: summary.state,
    relayId: summary.relayId,
    viewerCanBlockFromOrg: summary.viewerCanBlockFromOrg,
    viewerCanBlockPersonally: false,
    viewerCanReact: summary.viewerCanReact,
    viewerCanUnblockFromOrg: summary.viewerCanUnblockFromOrg,
    viewerCanUnblockPersonally: false,
    repository: summary.repository,
    url: summary.url,
  }
}

export function buildConversationReviewSummaries(summaries: readonly ReviewSummary[]): ConversationReviewSummary[] {
  return summaries.map(buildConversationReviewSummary)
}
